import React, { Fragment, useEffect, useRef } from 'react';
import {
    useTranslate,
    BulkDeleteButton,
    TextField,
    ShowButton,
    EditButton,
    ChipField,
    DateField,
    Filter,
    TextInput,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    ReferenceField,
    FunctionField,
    SelectArrayInput,
    List,
    Datagrid,
    useListContext,
} from 'react-admin';
import { makeStyles, Chip } from '@material-ui/core';
import CustomSearchInput from '../components/CustomSearchInput';
import BoxitChipField from '../components/BoxitChipField';
import UserReferenceField from '../components/UserReferenceField';
import { StorageStatus } from '../constants';
import CountryField from '../components/CountryField';
import MoneyField from '../components/MoneyField';
import BoxitPagination from '../components/BoxitPagination';
import WarehouseField from './components/WarehouseField';
import StorageTypeField from '../components/StorageTypeField';
import StorageRecordField from '../components/StorageRecordField';
import BulkEdit from './components/BulkEdit';
import CreateRequest from './components/CreateRequest';
import DamageField from '../components/DamageField';
import { ListProps, Storage, StorageType } from '../types';
import { StorageRecordStatus } from '../constants';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import NameField from '../requests/components/NameField';
import NameFieldList from '../requests/components/NameFieldList';

const useQuickFilterStyles = makeStyles(theme => ({
    chip: {
        marginBottom: theme.spacing(1),
    },
}));

const BulkActionButtons = props => (
    <Fragment>
        <CreateRequest {...props} />
        <BulkEdit {...props} />
        <BulkDeleteButton undoable={false} {...props} />
    </Fragment>
);

const QuickFilter = ({ source, label, defaultValue }) => {
    const translate = useTranslate();
    const classes = useQuickFilterStyles();
    return <Chip className={classes.chip} label={translate(label)} />;
};

const StorageFilter = props => {
    return (
        <Filter variant="outlined" {...props}>
            <CustomSearchInput alwaysOn />
            <ReferenceInput
                source="partnerId"
                reference="partners"
                resource="partners"
                label="Partner"
            >
                <SelectInput />
            </ReferenceInput>

            <SelectArrayInput
                style={{ minWidth: '10rem' }}
                source="status"
                choices={Object.keys(StorageStatus).map(c => ({
                    id: c,
                    name: c,
                }))}
            />

            <QuickFilter source="isPart" label="Parts" defaultValue={true} />

            <ReferenceInput
                source="storagetypeId"
                reference="storage-types"
                resource="storage-types"
                label="Type"
            >
                <AutocompleteInput
                    optionText={(choice: StorageType) =>
                        choice.code ? `${choice.code} ${choice.name}` : ''
                    }
                    matchSuggestion={(
                        filterValue: string,
                        choice: StorageType
                    ) =>
                        choice.code.match(new RegExp(filterValue, 'i')) ||
                        choice.name.match(new RegExp(filterValue, 'i'))
                    }
                    shouldRenderSuggestions={(val: string) =>
                        val.trim().length > 1
                    }
                />
            </ReferenceInput>

            <ReferenceInput
                source="userId"
                reference="users"
                resource="users"
                label="Customer"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={user => (user.name ? user.name : user.email)}
                />
            </ReferenceInput>

            <ReferenceInput
                source="roomId"
                reference="rooms"
                resource="rooms"
                label="Room"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={room => (room.name ? room.name : room.id)}
                />
            </ReferenceInput>

            <ReferenceInput
                source="warehouseId"
                reference="warehouses"
                resource="warehouses"
                label="Warehouse"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={warehouse =>
                        warehouse.name ? warehouse.name : warehouse.id
                    }
                />
            </ReferenceInput>

            <ReferenceInput
                source="locationId"
                reference="locations"
                resource="locations"
                label="Location"
            >
                <AutocompleteInput
                    shouldRenderSuggestions={val => val.trim().length > 2}
                    optionText={location =>
                        location.path ? location.path : location.id
                    }
                />
            </ReferenceInput>
        </Filter>
    );
};

const LocationField = ({
    label,
    record,
    ...props
}: {
    label: string;
    record?: Storage;
    props?: any;
}) =>
    record ? (
        <StorageRecordField label={label} storageId={record.id} {...props} />
    ) : null;

const StoragePartSelectionSync = () => {
    const { selectedIds = [], data = {}, onSelect } = useListContext();
    const prevSelectedIdsRef = useRef<any[]>(selectedIds || []);
    const internalSyncRef = useRef(false);

    useEffect(() => {
        const normalizeIds = (ids: any[]) =>
            (ids || []).map(id => Number(id)).filter(id => !Number.isNaN(id));

        const currentSelected = normalizeIds(selectedIds as any[]);
        const prevSelected = normalizeIds(prevSelectedIdsRef.current as any[]);

        if (internalSyncRef.current) {
            internalSyncRef.current = false;
            prevSelectedIdsRef.current = selectedIds as any[];
            return;
        }

        const prevSet = new Set(prevSelected);
        const currentSet = new Set(currentSelected);
        const unchanged =
            prevSet.size === currentSet.size &&
            currentSelected.every(id => prevSet.has(id));

        if (unchanged) {
            prevSelectedIdsRef.current = selectedIds as any[];
            return;
        }

        let toggledId: number | null = null;
        let isChecked = false;

        for (const id of currentSet) {
            if (!prevSet.has(id)) {
                toggledId = id;
                isChecked = true;
                break;
            }
        }
        if (toggledId === null) {
            for (const id of prevSet) {
                if (!currentSet.has(id)) {
                    toggledId = id;
                    isChecked = false;
                    break;
                }
            }
        }

        if (toggledId === null) {
            prevSelectedIdsRef.current = selectedIds as any[];
            return;
        }

        const toggledRecord = (data as any)[toggledId];
        if (!toggledRecord) {
            prevSelectedIdsRef.current = selectedIds as any[];
            return;
        }

        const parentId = toggledRecord.isPart
            ? Number(toggledRecord.parentId)
            : Number(toggledRecord.id);

        if (Number.isNaN(parentId)) {
            prevSelectedIdsRef.current = selectedIds as any[];
            return;
        }

        const nextSet = new Set(currentSet);

        Object.values(data as any).forEach((storage: any) => {
            const storageId = Number(storage?.id);
            if (Number.isNaN(storageId)) return;

            const belongsToGroup =
                storageId === parentId ||
                (storage?.isPart && Number(storage?.parentId) === parentId);

            if (!belongsToGroup) return;
            if (isChecked) nextSet.add(storageId);
            else nextSet.delete(storageId);
        });

        const nextIds = Array.from(nextSet);
        const nextSetCheck = new Set(nextIds);
        const changed =
            nextSetCheck.size !== currentSet.size ||
            !nextIds.every(id => currentSet.has(id));

        if (changed && onSelect) {
            internalSyncRef.current = true;
            onSelect(nextIds);
            prevSelectedIdsRef.current = nextIds;
            return;
        }

        prevSelectedIdsRef.current = selectedIds as any[];
    }, [selectedIds, data, onSelect]);

    return null;
};

const StorageList: React.FC<ListProps<Storage>> = ({
    permissions = [],
    ...props
}) => {
    // Column configuration with default visibility and order
    const defaultColumns: ColumnConfig[] = [
        // Default visible columns (essential information)
        { id: 'id', label: 'ID', visible: true, order: 0 },
        { id: 'serial', label: 'Serial', visible: true, order: 1 },
        { id: 'code', label: 'Code', visible: true, order: 2 },
        { id: 'name', label: 'Name', visible: true, order: 3 },
        { id: 'type', label: 'Type', visible: true, order: 4 },
        { id: 'customer', label: 'Customer', visible: true, order: 5 },
        { id: 'status', label: 'Status', visible: true, order: 6 },
        { id: 'qty', label: 'Quantity', visible: true, order: 7 },
        { id: 'size', label: 'CBM', visible: true, order: 8 },
        { id: 'show', label: 'Show', visible: true, order: 9 },

        // Optional columns (visible: false by default)
        { id: 'createdAt', label: 'Created At', visible: false, order: 10 },
        { id: 'updatedAt', label: 'Updated At', visible: false, order: 11 },
        { id: 'damage', label: 'Damage', visible: false, order: 12 },
        { id: 'totalSize', label: 'Total Size', visible: false, order: 13 },
        {
            id: 'sizeEntered',
            label: 'Actual CBM Size',
            visible: false,
            order: 14,
        },
        {
            id: 'consumedSize',
            label: 'Consumed Size',
            visible: false,
            order: 15,
        },
        { id: 'length', label: 'Length', visible: false, order: 16 },
        { id: 'height', label: 'Height', visible: false, order: 17 },
        { id: 'width', label: 'Width', visible: false, order: 18 },
        { id: 'country', label: 'Country', visible: false, order: 19 },
        { id: 'from', label: 'From', visible: false, order: 20 },
        { id: 'to', label: 'To', visible: false, order: 21 },
        { id: 'order', label: 'Order', visible: false, order: 22 },
        { id: 'rent', label: 'Rent', visible: false, order: 23 },
        { id: 'currency', label: 'Currency', visible: false, order: 24 },
        { id: 'isPart', label: 'Is Part', visible: false, order: 25 },
        { id: 'parentId', label: 'Parent ID', visible: false, order: 26 },
        { id: 'description', label: 'Description', visible: false, order: 27 },
        {
            id: 'customerPacked',
            label: 'Customer Packed',
            visible: false,
            order: 28,
        },
        { id: 'revision', label: 'Revision', visible: false, order: 29 },
        {
            id: 'storagetype_code',
            label: 'Storage Type Code',
            visible: false,
            order: 30,
        },
        {
            id: 'storagetypeId',
            label: 'Storage Type ID',
            visible: false,
            order: 31,
        },
        { id: 'roomId', label: 'Room ID', visible: false, order: 32 },
        {
            id: 'storedQty',
            label: 'Stored Quantity',
            visible: false,
            order: 33,
        },
        { id: 'userId', label: 'User ID', visible: false, order: 34 },
        {
            id: 'affiliateUserId',
            label: 'Affiliate User ID',
            visible: false,
            order: 35,
        },
        { id: 'partnerId', label: 'Partner ID', visible: false, order: 36 },
        { id: 'countryId', label: 'Country ID', visible: false, order: 37 },
        { id: 'orderId', label: 'Order ID', visible: false, order: 38 },
        {
            id: 'storageTypeName',
            label: 'Storage Type Name',
            visible: false,
            order: 39,
        },
        {
            id: 'storageTypeSize',
            label: 'Storage Type Size',
            visible: false,
            order: 40,
        },
        {
            id: 'storageTypeInventory',
            label: 'Storage Type Inventory',
            visible: false,
            order: 41,
        },
        { id: 'userName', label: 'User Name', visible: false, order: 42 },
        {
            id: 'warehouse',
            label: 'Warehouse',
            visible: permissions.indexOf('partner') === -1 ? false : false,
            order: 43,
        },
        {
            id: 'locations',
            label: 'Locations',
            visible: permissions.indexOf('partner') === -1 ? false : false,
            order: 44,
        },
        {
            id: 'room',
            label: 'Room',
            visible: permissions.indexOf('partner') === -1 ? false : false,
            order: 45,
        },
        {
            id: 'edit',
            label: 'Edit',
            visible: permissions.indexOf('partner') === -1 ? false : false,
            order: 46,
        },
    ];

    // Render a column based on its ID
    const renderColumn = columnId => {
        switch (columnId) {
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'code':
                return <TextField key={columnId} source="code" />;
            case 'type':
                return (
                    <FunctionField
                        key={columnId}
                        label="Type"
                        render={record => {
                            return (
                                <div>
                                    <span>{record.storagetype?.code}</span>
                                    {record.isPart && <Chip label="part" />}
                                </div>
                            );
                        }}
                    />
                );
            case 'damage':
                return (
                    <DamageField
                        key={columnId}
                        source="damage"
                        label="Damage"
                    />
                );
            case 'name':
                return <NameFieldList key={columnId} label="Name" />;
            case 'qty':
                return <TextField key={columnId} source="qty" />;
            case 'size':
                return <TextField key={columnId} source="size" label="CBM" />;
            case 'totalSize':
                return (
                    <FunctionField
                        key={columnId}
                        label="Total size"
                        render={record =>
                            record.qty !== undefined &&
                            record.size !== undefined
                                ? record.qty * record.size
                                : ''
                        }
                    />
                );
            case 'sizeEntered':
                return (
                    <TextField
                        key={columnId}
                        source="sizeEntered"
                        label="Actual CBM Size (test)"
                    />
                );
            case 'consumedSize':
                return (
                    <TextField
                        key={columnId}
                        source="consumedSize"
                        label="Consumed Size (test)"
                    />
                );
            case 'length':
                return (
                    <TextField key={columnId} source="length" label="Length" />
                );
            case 'height':
                return (
                    <TextField key={columnId} source="height" label="Height" />
                );
            case 'width':
                return (
                    <TextField key={columnId} source="width" label="Width" />
                );
            case 'customer':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer"
                        render={record => {
                            return (
                                <div
                                    style={{
                                        width: '8rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    {record && record.userId && (
                                        <button
                                            style={{
                                                cursor: 'pointer',
                                                color: '#1890ff',
                                                outline: 'none',
                                            }}
                                            onClick={() => {
                                                window.location.href = `/#/users/${record.userId}/show`;
                                            }}
                                        >
                                            {record.user
                                                ? record.user.name
                                                : record.userId}
                                        </button>
                                    )}
                                </div>
                            );
                        }}
                    />
                );
            case 'country':
                return (
                    <FunctionField
                        key={columnId}
                        label="Country"
                        render={record => {
                            // Use countryName from API response if available, otherwise fall back to ID-based logic
                            return record.countryName
                                ? record.countryName
                                : record.countryId === 1
                                ? 'UAE'
                                : record.countryId === 2
                                ? 'Kuwait'
                                : 'KSA';
                        }}
                    />
                );
            case 'from':
                return (
                    <DateField key={columnId} locales="en-IN" source="from" />
                );
            case 'to':
                return <DateField key={columnId} locales="en-IN" source="to" />;
            case 'order':
                return (
                    <FunctionField
                        key={columnId}
                        label="Order"
                        render={record => {
                            return (
                                <div
                                    style={{
                                        width: '8rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'flex-start',
                                    }}
                                >
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            color: '#1890ff',
                                            outline: 'none',
                                        }}
                                        onClick={() => {
                                            window.location.href = `/#/orders/${record.orderId}/show`;
                                        }}
                                    >
                                        {record.orderId}
                                    </button>
                                </div>
                            );
                        }}
                    />
                );
            case 'status':
                return (
                    <FunctionField
                        key={columnId}
                        label="Status"
                        render={record => (
                            <BoxitChipField
                                record={record}
                                source="status"
                                list="storage"
                            />
                        )}
                    />
                );
            case 'warehouse':
                if (permissions.indexOf('partner') === -1) {
                    return (
                        <FunctionField
                            key={columnId}
                            label="Warehouse"
                            render={record => {
                                const storageRecord =
                                    record &&
                                    record.storagerecords &&
                                    !!record.storagerecords.length &&
                                    record.storagerecords
                                        .filter(
                                            i =>
                                                i.status ===
                                                StorageRecordStatus.in
                                        )
                                        .find(i => !!i);
                                return storageRecord ? (
                                    <button
                                        style={{
                                            cursor: 'pointer',
                                            color: '#1890ff',
                                            outline: 'none',
                                        }}
                                        onClick={() => {
                                            window.location.href = `/#/warehouses/${storageRecord.warehouseId}/show`;
                                        }}
                                    >
                                        {storageRecord.warehouse.name}
                                    </button>
                                ) : null;
                            }}
                        />
                    );
                }
                return null;
            case 'locations':
                if (permissions.indexOf('partner') === -1) {
                    return (
                        <FunctionField
                            key={columnId}
                            label="Locations"
                            render={record => {
                                return (
                                    <div
                                        style={{
                                            width: '8rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            justifyContent: 'flex-start',
                                        }}
                                    >
                                        {record.storagerecords
                                            .filter(
                                                storageRecord =>
                                                    storageRecord.orderId &&
                                                    storageRecord.storageId &&
                                                    storageRecord.status ===
                                                        StorageRecordStatus.in
                                            )
                                            .map((storageRecord, index) => {
                                                return storageRecord.location ? (
                                                    <button
                                                        key={`location-${index}-${storageRecord.locationId}`}
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#1890ff',
                                                            outline: 'none',
                                                        }}
                                                        onClick={() => {
                                                            window.location.href = `/#/locations/${storageRecord.locationId}/show`;
                                                        }}
                                                    >
                                                        {
                                                            storageRecord
                                                                .location.path
                                                        }
                                                    </button>
                                                ) : null;
                                            })}
                                    </div>
                                );
                            }}
                        />
                    );
                }
                return null;
            case 'room':
                if (permissions.indexOf('partner') === -1) {
                    return (
                        <FunctionField
                            key={columnId}
                            label="Room"
                            render={record => {
                                return record && record.room ? (
                                    <div>
                                        <span>{record.room.name}</span>
                                        <span
                                            style={{
                                                color: '#888',
                                                fontSize: '0.8em',
                                                marginLeft: '5px',
                                            }}
                                        >
                                            (ID: {record.roomId})
                                        </span>
                                    </div>
                                ) : record && record.roomId ? (
                                    <span>{record.roomId}</span>
                                ) : null;
                            }}
                        />
                    );
                }
                return null;
            case 'edit':
                if (permissions.indexOf('partner') === -1) {
                    return (
                        <EditButton
                            key={columnId}
                            color="secondary"
                            style={{ padding: 0 }}
                        />
                    );
                }
                return null;
            case 'show':
                return <ShowButton key={columnId} style={{ padding: 0 }} />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        source="createdAt"
                        label="Created At"
                        locales="en-GB"
                    />
                );
            case 'updatedAt':
                return (
                    <DateField
                        key={columnId}
                        source="updatedAt"
                        label="Updated At"
                        locales="en-GB"
                        showTime
                    />
                );
            case 'rent':
                return (
                    <FunctionField
                        key={columnId}
                        label="Rent"
                        render={record => {
                            if (!record.rent) return '-';
                            const currency = record.currency || 'USD';
                            const rent = record.rent / 1000; // Divide by 1000 like other lists
                            return `${currency} ${rent.toLocaleString()}`;
                        }}
                    />
                );
            case 'currency':
                return (
                    <TextField
                        key={columnId}
                        source="currency"
                        label="Currency"
                    />
                );
            case 'isPart':
                return (
                    <FunctionField
                        key={columnId}
                        label="Is Part"
                        render={record => (
                            <Chip
                                label={record.isPart ? 'Yes' : 'No'}
                                color={record.isPart ? 'primary' : 'default'}
                                size="small"
                            />
                        )}
                    />
                );
            case 'parentId':
                return (
                    <TextField
                        key={columnId}
                        source="parentId"
                        label="Parent ID"
                    />
                );
            case 'description':
                return (
                    <FunctionField
                        key={columnId}
                        label="Description"
                        render={record => (
                            <div
                                style={{
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {record.description || '-'}
                            </div>
                        )}
                    />
                );
            case 'customerPacked':
                return (
                    <FunctionField
                        key={columnId}
                        label="Customer Packed"
                        render={record => (
                            <Chip
                                label={record.customerPacked ? 'Yes' : 'No'}
                                color={
                                    record.customerPacked
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'revision':
                return (
                    <TextField
                        key={columnId}
                        source="revision"
                        label="Revision"
                    />
                );
            case 'storagetype_code':
                return (
                    <TextField
                        key={columnId}
                        source="storagetype_code"
                        label="Storage Type Code"
                    />
                );
            case 'storagetypeId':
                return (
                    <TextField
                        key={columnId}
                        source="storagetypeId"
                        label="Storage Type ID"
                    />
                );
            case 'roomId':
                return (
                    <TextField key={columnId} source="roomId" label="Room ID" />
                );
            case 'storedQty':
                return (
                    <TextField
                        key={columnId}
                        source="storedQty"
                        label="Stored Quantity"
                    />
                );
            case 'userId':
                return (
                    <TextField key={columnId} source="userId" label="User ID" />
                );
            case 'affiliateUserId':
                return (
                    <TextField
                        key={columnId}
                        source="affiliateUserId"
                        label="Affiliate User ID"
                    />
                );
            case 'partnerId':
                return (
                    <TextField
                        key={columnId}
                        source="partnerId"
                        label="Partner ID"
                    />
                );
            case 'countryId':
                return (
                    <TextField
                        key={columnId}
                        source="countryId"
                        label="Country ID"
                    />
                );
            case 'orderId':
                return (
                    <TextField
                        key={columnId}
                        source="orderId"
                        label="Order ID"
                    />
                );
            case 'storageTypeName':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Type Name"
                        render={record => record.storagetype?.name || '-'}
                    />
                );
            case 'storageTypeSize':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Type Size"
                        render={record => record.storagetype?.size || '-'}
                    />
                );
            case 'storageTypeInventory':
                return (
                    <FunctionField
                        key={columnId}
                        label="Storage Type Inventory"
                        render={record => (
                            <Chip
                                label={
                                    record.storagetype?.inventory ? 'Yes' : 'No'
                                }
                                color={
                                    record.storagetype?.inventory
                                        ? 'primary'
                                        : 'default'
                                }
                                size="small"
                            />
                        )}
                    />
                );
            case 'userName':
                return (
                    <FunctionField
                        key={columnId}
                        label="User Name"
                        render={record => record.user?.name || '-'}
                    />
                );
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Storages',
        permissions,
        defaultColumns,
        storageKey: 'storageListColumns',
        renderColumn,
        bulkActionButtons: <BulkActionButtons />,
        filtersComponent: <StorageFilter />,
        sort: { field: 'id', order: 'DESC' },
        paginationComponent: <BoxitPagination />,
        perPage: 10,
        listContextComponent: <StoragePartSelectionSync />,
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'storages',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

StorageList.defaultProps = {
    resource: 'storages',
    location: { search: '' },
};

export default StorageList;
